// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState, type CSSProperties } from 'react'
import { Loader2, UserPlus, Users, Star, Plus, X } from 'lucide-react'
import * as api from '../../api/client'
import SearchableSelect from '../../components/common/SearchableSelect'
import { friendlyError } from '../../api/errors'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

// D-UI-2: a funnel on every column of the roster. Values are what the cell shows.
const NURSE_FUNNELS: FunnelColumn<api.NurseRow>[] = [
  { key: 'nurse', label: 'Nurse', value: n => n.display_name },
  { key: 'territory', label: 'Territory', value: n => n.territory_zips || '—' },
  { key: 'competencies', label: 'Competencies', value: n => n.competencies || '—' },
  { key: 'teams', label: 'Teams', value: n => n.team_count },
  { key: 'status', label: 'Status', value: n => (n.active ? 'active' : 'inactive') },
]

/**
 * D-CRX-47 POT-6 — nurse &amp; team management (ported from asp-pot's roster + teams). Add / edit /
 * deactivate roster nurses (agency = login-less), create teams, and assign nurses to teams with a
 * per-team manager flag ({name, managerIds[], nurseIds[]} in asp-pot).
 */
export default function CareRxNursesTeams({ agencyId }: { agencyId?: string }) {
  const [nurses, setNurses] = useState<api.NurseRow[]>([])
  const [teams, setTeams] = useState<api.NurseTeam[]>([])
  const [loading, setLoading] = useState(true)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  // add-nurse form
  const [nName, setNName] = useState(''); const [nZips, setNZips] = useState(''); const [nComp, setNComp] = useState('')
  // add-team form
  const [tName, setTName] = useState('')
  const fx = useTableFunnels(nurses, NURSE_FUNNELS)

  const reload = async () => {
    try {
      const [n, t] = await Promise.all([api.listCareRxNurses(agencyId), api.listCareRxNurseTeams(agencyId)])
      setNurses(n.items); setTeams(t.items)
    } catch (e: any) { setMsg(friendlyError(e)) } finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [agencyId])  // eslint-disable-line react-hooks/exhaustive-deps

  const addNurse = async () => {
    if (!nName.trim()) { setMsg('Nurse name is required.'); return }
    setBusy(true); setMsg('')
    try { await api.createCareRxNurse({ display_name: nName.trim(), territory_zips: nZips.trim() || undefined, competencies: nComp.trim() || undefined, agency_id: agencyId }); setNName(''); setNZips(''); setNComp(''); reload() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const toggleNurse = async (n: api.NurseRow) => {
    setBusy(true); try { await api.updateCareRxNurse(n.id, { active: !n.active }); reload() } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const addTeam = async () => {
    if (!tName.trim()) { setMsg('Team name is required.'); return }
    setBusy(true); setMsg('')
    try { await api.createCareRxNurseTeam({ name: tName.trim(), agency_id: agencyId }); setTName(''); reload() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const addMember = async (teamId: string, rosterId: string, asManager: boolean) => {
    if (!rosterId) return
    setBusy(true); setMsg('')
    try { await api.addCareRxTeamMember(teamId, { roster_id: rosterId, is_manager: asManager }); reload() }
    catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }
  const removeMember = async (teamId: string, rosterId: string) => {
    setBusy(true); try { await api.removeCareRxTeamMember(teamId, rosterId); reload() } catch (e: any) { setMsg(friendlyError(e)) } finally { setBusy(false) }
  }

  if (loading) return <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: muted.color, padding: 20 }}><Loader2 size={16} className="spin" /> Loading nurses &amp; teams…</div>

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      {msg && <div style={{ fontSize: 12.5, color: '#b91c1c' }}>{msg}</div>}

      {/* Roster */}
      <div>
        <div style={cardTitle}><Users size={16} /> Nurse roster <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>{nurses.length} nurses</span></div>
        <div style={{ ...card, marginTop: 8 }}>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 10 }}>
            <label style={lbl}>Name<input value={nName} onChange={e => setNName(e.target.value)} placeholder="Nurse name" style={input} /></label>
            <label style={lbl}>Territory zips<input value={nZips} onChange={e => setNZips(e.target.value)} placeholder="84009,84095" style={input} /></label>
            <label style={lbl}>Competencies<input value={nComp} onChange={e => setNComp(e.target.value)} placeholder="IV infusion, biologics" style={input} /></label>
            <button disabled={busy} onClick={addNurse} style={primaryBtn}><UserPlus size={13} /> Add nurse</button>
          </div>
          {fx.chips}
          <table {...fx.tableProps} style={table}>
            <thead><tr><th style={th}>Nurse {fx.funnel('nurse')}</th><th style={th}>Territory {fx.funnel('territory')}</th><th style={th}>Competencies {fx.funnel('competencies')}</th><th style={th}>Teams {fx.funnel('teams')}</th><th style={th}>Status {fx.funnel('status')}</th><th style={th}></th></tr></thead>
            <tbody>
              {fx.rows.length === 0 && <tr><td style={td} colSpan={6}><span style={muted}>{nurses.length === 0 ? 'No nurses yet — add one above.' : 'No rows match the current filters.'}</span></td></tr>}
              {fx.rows.map(n => (
                <tr key={n.id} style={{ opacity: n.active ? 1 : 0.5 }}>
                  <td style={td}><b>{n.display_name}</b></td>
                  <td style={td}>{n.territory_zips || '—'}</td>
                  <td style={td}>{n.competencies || '—'}</td>
                  <td style={td}>{n.team_count}</td>
                  <td style={td}>{n.active ? <span style={okBadge}>active</span> : <span style={offBadge}>inactive</span>}</td>
                  <td style={{ ...td, textAlign: 'right' }}>
                    <button disabled={busy} onClick={() => toggleNurse(n)} style={linkBtn}>{n.active ? 'Deactivate' : 'Reactivate'}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Teams */}
      <div>
        <div style={cardTitle}><Users size={16} /> Teams <span style={{ ...muted, fontSize: 12, fontWeight: 400 }}>{teams.length}</span></div>
        <div style={{ ...card, marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
          <input value={tName} onChange={e => setTName(e.target.value)} placeholder="New team name" style={{ ...input, maxWidth: 240 }} />
          <button disabled={busy} onClick={addTeam} style={primaryBtn}><Plus size={13} /> Create team</button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 10, marginTop: 10 }}>
          {teams.map(t => <TeamCard key={t.id} team={t} nurses={nurses} busy={busy} onAdd={addMember} onRemove={removeMember} />)}
          {teams.length === 0 && <div style={{ ...muted, fontSize: 12.5 }}>No teams yet — create one above, then add nurses to it.</div>}
        </div>
      </div>
    </div>
  )
}

function TeamCard({ team, nurses, busy, onAdd, onRemove }: {
  team: api.NurseTeam; nurses: api.NurseRow[]; busy: boolean
  onAdd: (teamId: string, rosterId: string, asManager: boolean) => void
  onRemove: (teamId: string, rosterId: string) => void
}) {
  const [pick, setPick] = useState('')
  const memberIds = new Set(team.members.map(m => m.roster_id))
  const available = nurses.filter(n => n.active && !memberIds.has(n.id))
  return (
    <div style={card}>
      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{team.name}</div>
      {team.members.length === 0 && <div style={{ ...muted, fontSize: 12 }}>No members yet.</div>}
      {team.members.map(m => (
        <div key={m.roster_id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, padding: '3px 0', borderTop: '1px solid var(--color-border, #eee)' }}>
          {m.is_manager && <Star size={12} color="#0a7a1d" />}
          <span>{m.display_name}</span>
          {m.is_manager && <span style={{ ...okBadge, marginLeft: 2 }}>manager</span>}
          <button disabled={busy} onClick={() => onRemove(team.id, m.roster_id)} title="Remove" style={{ marginLeft: 'auto', background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', padding: 0 }}><X size={13} /></button>
        </div>
      ))}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, alignItems: 'center' }}>
        <SearchableSelect value={pick} onChange={setPick} style={{ ...input, fontSize: 12, minWidth: 170 }}
          placeholder="+ add nurse…" ariaLabel="Add nurse"
          options={[{ value: '', label: '+ add nurse…' }, ...available.map(n => ({ value: n.id, label: n.display_name ?? n.id }))]} />
        <button disabled={busy || !pick} onClick={() => { onAdd(team.id, pick, false); setPick('') }} style={smallBtn}>Add</button>
        <button disabled={busy || !pick} onClick={() => { onAdd(team.id, pick, true); setPick('') }} title="Add as team manager" style={smallBtn}>+ Mgr</button>
      </div>
    </div>
  )
}

const muted: CSSProperties = { color: 'var(--color-text-soft, #6b7280)' }
const cardTitle: CSSProperties = { fontSize: 15, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }
const card: CSSProperties = { background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, padding: '12px 14px' }
const table: CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }
const th: CSSProperties = { textAlign: 'left', padding: '4px 8px', fontSize: 10.5, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--color-text-soft, #6b7280)', borderBottom: '1px solid var(--color-border, #e5e7eb)' }
const td: CSSProperties = { padding: '5px 8px', borderBottom: '1px solid var(--color-border, #f1f5f9)', verticalAlign: 'top' }
const lbl: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, fontWeight: 600, color: 'var(--color-text-soft, #6b7280)' }
const input: CSSProperties = { padding: '6px 8px', border: '1px solid var(--color-border, #cfd7dd)', borderRadius: 6, fontSize: 13, minWidth: 130 }
const primaryBtn: CSSProperties = { display: 'inline-flex', alignItems: 'center', gap: 5, padding: '7px 12px', background: 'var(--color-primary, #0d9624)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12.5, fontWeight: 600, cursor: 'pointer' }
const smallBtn: CSSProperties = { padding: '5px 9px', background: 'transparent', border: '1px solid var(--color-primary, #0d9624)', color: 'var(--color-primary, #0d9624)', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }
const linkBtn: CSSProperties = { background: 'none', border: 'none', color: 'var(--color-primary, #0d9624)', cursor: 'pointer', fontSize: 12, padding: 0 }
const okBadge: CSSProperties = { fontSize: 10, fontWeight: 800, color: '#0a7a1d', background: '#e9f7ec', border: '1px solid #b9e3c3', borderRadius: 9, padding: '1px 7px' }
const offBadge: CSSProperties = { fontSize: 10, fontWeight: 800, color: '#8a6d1f', background: '#fdf6e3', border: '1px solid #e8c97a', borderRadius: 9, padding: '1px 7px' }
